import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import ProgressBar from '../components/ProgressBar';
import { useAppState } from '../state/AppState';
import { getProviderNameFromAvailability } from '../services/availabilityDataService';
import { openFindProviderPage } from '../services/providerService';
import { saveSession } from '../services/sessionService';
import { initializeConfig } from '../services/configService';

/**
 * ScreenE - Information confirmation screen.
 *
 * Shows pre-filled appointment information based on user selections
 * and allows confirmation, changes, or provider selection.
 */
const ScreenE: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppState();

  const handleConfirm = () => {
    // Navigate to calendar/time selection
    navigate('/m');
  };

  const handleChangeInformation = async () => {
    // Update the app state with cleared values
    dispatch({ type: 'SET_STATE_STRING', payload: '' });        // Screen F
    dispatch({ type: 'SET_SERVICE', payload: '' });              // Screen G  
    dispatch({ type: 'SET_INSURANCE', payload: '' });           // Screen H
    dispatch({ type: 'SET_LOCATION', payload: '' });             // Screen I
    dispatch({ type: 'SET_LOCATION_CODE', payload: '' });       // Screen I
    dispatch({ type: 'SET_LOCATION_ADDRESS', payload: '' });     // Screen I
    dispatch({ type: 'SET_REASON', payload: '' });              // Screen J
    dispatch({ type: 'SET_AGE_GROUP', payload: '' });           // Screen K
    
    // Create cleared state and save to remote session
    const clearedState = {
      // Keep only the fields we want to preserve
      providerId: state.providerId,
      visitType: state.visitType,
      appointmentDate: state.appointmentDate,
      appointmentTime: state.appointmentTime,
      appointmentDay: state.appointmentDay,
      profileId: state.profileId,
      columnId: state.columnId,
      licenseKey: state.licenseKey,
      existingClient: state.existingClient,
      availableLocations: state.availableLocations,
      reservationStartTime: state.reservationStartTime,
      timerStart: state.reservationStartTime ? new Date(state.reservationStartTime).toISOString() : null,
      timestamp: Date.now(),
      // Clear all fields from screens F through K
      stateString: null,        // Screen F
      service: null,            // Screen G  
      insurance: null,         // Screen H
      location: null,          // Screen I
      locationCode: null,      // Screen I
      locationAddress: null,   // Screen I
      reason: null,            // Screen J
      ageGroup: null           // Screen K
    };
    try {
      // Get the remote API URL from config
      const config = await initializeConfig();
      const baseUrl = config.remoteApiUrl || 'http://localhost:5293/';
      
      const response = await fetch(`${baseUrl}/api/Session/set/data`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(clearedState)
      });
      
      if (response.ok) {
      } else {
      }
    } catch (error) {
    }
    
    // Navigate back to Screen D to start over
    navigate('/d');
  };

  const handleChooseDifferentProvider = () => {
    openFindProviderPage(state);
  };

  // Get availability data for the selected region
  const regionAvailabilityData = useMemo(() => {
    if (!state.availabilities || !state.stateString) return [];
    
    return state.availabilities.filter(availability => 
      availability.region === state.stateString
    );
  }, [state.availabilities, state.stateString]);

  // Helper function to get display text for age group from availability data
  const getAgeGroupDisplay = (ageGroup: string | null) => {
    if (!ageGroup) return 'Not specified';
    
    // If it's already a full age group string (like "School-Aged (6-12)"), return as-is
    if (ageGroup.includes('(') && ageGroup.includes(')')) {
      return ageGroup;
    }
    
    // Try to find matching age group from availability data
    const availableAgeGroups = new Set<string>();
    regionAvailabilityData.forEach(availability => {
      if (availability.ageRangeGroups) {
        const ageGroups = availability.ageRangeGroups.split(';').map(ag => ag.trim());
        ageGroups.forEach(ag => availableAgeGroups.add(ag));
      }
    });
    
    // Find the best match from available age groups
    const matchingAgeGroup = Array.from(availableAgeGroups).find(ag => 
      ag.toLowerCase().includes(ageGroup.toLowerCase()) || 
      ageGroup.toLowerCase().includes(ag.toLowerCase())
    );
    
    return matchingAgeGroup || ageGroup;
  };

  // Helper function to get display text for service from availability data
  const getServiceDisplay = (service: string | null) => {
    if (!service) return 'Not specified';
    
    // Try to find matching service from availability data
    const availableServices = new Set<string>();
    regionAvailabilityData.forEach(availability => {
      if (availability.providerServices) {
        const services = availability.providerServices.split(';').map(s => s.trim());
        services.forEach(s => availableServices.add(s));
      }
    });
    
    // Find the best match from available services
    const matchingService = Array.from(availableServices).find(s => 
      s.toLowerCase().includes(service.toLowerCase()) || 
      service.toLowerCase().includes(s.toLowerCase())
    );
    
    return matchingService || service;
  };

  // Format modalities: semicolon-delimited, display as comma and space separated
  const getModalitiesDisplay = (modalities: string | null) => {
    if (!modalities || !modalities.trim()) return 'Not specified';
    return modalities
      .split(';')
      .map((v) => v.trim())
      .filter(Boolean)
      .join(', ');
  };

  // Helper function to get display text for reason from availability data
  const getReasonDisplay = (reason: string | null) => {
    if (!reason) return 'Not specified';
    
    // If it's already a specialty name (like "Anxiety" or "Depression"), return as-is
    if (reason && !reason.startsWith('specialty-') && !reason.startsWith('reason-')) {
      return reason;
    }
    
    // Try to find matching specialty from availability data
    const availableSpecialties = new Set<string>();
    regionAvailabilityData.forEach(availability => {
      if (availability.specialties) {
        const specialties = availability.specialties.split(';').map(s => s.trim());
        specialties.forEach(s => availableSpecialties.add(s));
      }
    });
    
    // Find the best match from available specialties
    const matchingSpecialty = Array.from(availableSpecialties).find(s => 
      s.toLowerCase().includes(reason.toLowerCase()) || 
      reason.toLowerCase().includes(s.toLowerCase())
    );
    
    return matchingSpecialty || reason;
  };

  // Helper function to get desired time with fallback to sessionData.ioiPayload
  const getDesiredTime = () => {
    // First try state.appointmentTime
    if (state.appointmentTime) {
      return state.appointmentTime;
    }
    
    // Fallback to sessionData.ioiPayload.cs_day + ' ' + cs_time
    if (state.sessionData && state.sessionData.data) {
      try {
        // Try to get ioiPayload from data.data (parsed JSON string)
        if (state.sessionData.data.data) {
          let parsedData;
          if (typeof state.sessionData.data.data === 'string') {
            parsedData = JSON.parse(state.sessionData.data.data);
          } else {
            parsedData = state.sessionData.data.data;
          }
          
          if (parsedData.ioiPayload) {
            const ioiPayload = parsedData.ioiPayload;
            const day = ioiPayload.cs_day;
            const time = ioiPayload.cs_time;
            if (day && time) {
              return `${day} ${time}`;
            }
          }
        }
        
        // Try to get ioiPayload from data.ioiPayload (JSON string)
        if (state.sessionData.data.ioiPayload) {
          let ioiPayload;
          if (typeof state.sessionData.data.ioiPayload === 'string') {
            ioiPayload = JSON.parse(state.sessionData.data.ioiPayload);
          } else {
            ioiPayload = state.sessionData.data.ioiPayload;
          }
          
          const day = ioiPayload.cs_day;
          const time = ioiPayload.cs_time;
          if (day && time) {
            return `${day} ${time}`;
          }
        }
      } catch (error) {
      }
    }
    
    return 'Not specified';
  };

  return (
    <div className="screen-bg">
      <div className="main-content-container">
        <div className="mtg-back-btn-align">
          <BackButton onClick={() => navigate(-1)} />
        </div>
        <main className="mtg-screen-main" id="main-content">
          <div className="mtg-screen-card mb-18">
            <h2 className="mtg-screen-question mb-8">
              Is this correct?
            </h2>
            <p className="text-center text-soft mb-24" style={{
               fontSize: '0.95rem', 
               lineHeight: 1.4
             }}>
                Based on your selections, we have<br />prefilled the following information<br />to meet with {getProviderNameFromAvailability(state.availabilities, null) || 'your selected provider'}.
             </p>
            
            <div className="mb-24" style={{
               background: '#fff',
               borderRadius: 3,
               padding: '20px 24px',
               boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
               border: '1px solid #e0e0e0',
               fontSize: '0.9rem',
               lineHeight: 1.5,
               width: 'calc(100% - 96px)',
               margin: '0 auto'
             }}>
              <div className="mb-16">
                 <span style={{ fontWeight: 'bold' }}>Selected Provider:</span> {getProviderNameFromAvailability(state.availabilities, null) || 'Not specified'}
              </div>
              <div className="mb-16">
                <span style={{ fontWeight: 'bold' }}>State:</span> {state.stateString || 'Not specified'}
              </div>
              <div className="mb-16">
                <span style={{ fontWeight: 'bold' }}>Service:</span> {getServiceDisplay(state.service)}
              </div>
              <div className="mb-16">
                <span style={{ fontWeight: 'bold' }}>Insurance:</span> {state.insurance || 'Not specified'}
              </div>
              <div className="mb-16">
                <span style={{ fontWeight: 'bold' }}>Location:</span> {state.location ? state.location.replace(/\+/g, ' ') : 'Not specified'}
              </div>
              <div className="mb-16">
                <span style={{ fontWeight: 'bold' }}>Desired Time:</span> {getDesiredTime()}
              </div>
              {state.appointmentDay && (
                <div className="mb-16">
                  <span style={{ fontWeight: 'bold' }}>Desired Day:</span> {state.appointmentDay}
                </div>
              )}
              <div className="mb-16">
                <span style={{ fontWeight: 'bold' }}>Age Range:</span> {getAgeGroupDisplay(state.ageGroup)}
              </div>
              <div className="mb-16">
                <span style={{ fontWeight: 'bold' }}>Modalities:</span> {getModalitiesDisplay(state.modalities)}
              </div>
              <div className="mb-0">
                <span style={{ fontWeight: 'bold' }}>Reason For Visit:</span> {getReasonDisplay(state.reason)}
              </div>
            </div>

           <div className="button-container">
              <button 
                className="wide-blue-btn" 
                onClick={handleConfirm}
                aria-label="Confirm appointment information"
              >
                Confirm
              </button>
              
              <button 
                className="wide-blue-btn" 
                onClick={handleChangeInformation}
                aria-label="Change appointment information"
              >
                Change Information
              </button>
              
              <button 
                className="wide-blue-btn" 
                onClick={handleChooseDifferentProvider}
                aria-label="Choose a different provider"
              >
                Choose a Different Provider
              </button>
            </div>

            <ProgressBar progress={70}/>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ScreenE;
